import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, AccessTime, Group, Title } from '@mui/icons-material';
import { IEventoCalendario, IAmenidad } from '../../../models/reservas';

interface NewEventFormProps {
  activeForm: boolean;
  setActiveForm: (activeForm: boolean) => void;
  setEvents: (eventos:IEventoCalendario[] | ((prev: IEventoCalendario[]) => IEventoCalendario[])) => void;
  dateStr: string;
  infoAmenidad: IAmenidad | undefined;
  alertAction?: (msg:string) => void;
}

const getEndTime = (date:string, hours:number) => {
  const [mainDate, time] = date.split('T');
  const timeHour = Number(time.split(':')[0])
  const endTime = timeHour + hours;
  return `${mainDate}T${endTime.toString().padStart(2, '0')}:00:00`
};

export const NewEventForm = ({ activeForm, setActiveForm, setEvents, dateStr, infoAmenidad, alertAction = (m) => alert(m) }: NewEventFormProps) => {
  const [eventTitle, setEventTitle] = React.useState<string>('');
  const [eventTime, setEventTime] = React.useState<string>('');
  const [personCount, setPersonCount] = React.useState<string>('');
  const { 
    inicio_Amenidad, 
    fin_Amenidad, 
    tiempo_Maximo, 
    usar_Limites, 
    li_1, li_2, li_3, 
    ls_1, ls_2, ls_3, 
    costo_1, costo_2, costo_3 
  } = infoAmenidad ?? {
    inicio_Amenidad: 0,
    fin_Amenidad: 24,
    tiempo_Maximo: 24,
    usar_Limites: false,
    li_1: 0, li_2: 0, li_3: 0,
    ls_1: 0, ls_2: 0, ls_3: 0,
    costo_1: 0, costo_2: 0, costo_3: 0
  };

  // Calcular precio según el rango de personas
  const calcularPrecio = React.useCallback((cantidad: number) => {
    if (li_1 && ls_1 && cantidad >= li_1 && cantidad <= ls_1) return costo_1 || 0;
    if (li_2 && ls_2 && cantidad >= li_2 && cantidad <= ls_2) return costo_2 || 0;
    if (li_3 && ls_3 && cantidad >= li_3 && cantidad <= ls_3) return costo_3 || 0;
    return 0; // Fuera de rangos
  }, [li_1, li_2, li_3, ls_1, ls_2, ls_3, costo_1, costo_2, costo_3]);

  // Obtener información del rango actual
  const obtenerRangoInfo = React.useCallback((cantidad: number) => {
    if (li_1 && ls_1 && cantidad >= li_1 && cantidad <= ls_1) return { rango: 1, precio: costo_1 || 0, desde: li_1, hasta: ls_1 };
    if (li_2 && ls_2 && cantidad >= li_2 && cantidad <= ls_2) return { rango: 2, precio: costo_2 || 0, desde: li_2, hasta: ls_2 };
    if (li_3 && ls_3 && cantidad >= li_3 && cantidad <= ls_3) return { rango: 3, precio: costo_3 || 0, desde: li_3, hasta: ls_3 };
    return null;
  }, [li_1, li_2, li_3, ls_1, ls_2, ls_3, costo_1, costo_2, costo_3]);

  // Obtener el límite máximo permitido
  const obtenerLimiteMaximo = React.useCallback(() => {
    const limites = [ls_1, ls_2, ls_3].filter((limite): limite is number => limite !== undefined && limite > 0);
    return limites.length > 0 ? Math.max(...limites) : 0;
  }, [ls_1, ls_2, ls_3]);

  // Calcular precio total cuando usar_Limites está activo
  const precioTotal = React.useMemo(() => {
    if (!usar_Limites || !personCount) return 0;
    const cantidad = parseInt(personCount) || 0;
    const precio = calcularPrecio(cantidad);
    return precio;
  }, [usar_Limites, personCount, calcularPrecio]);

  const handleCreateEvent = (evTitle:string, evTime:number, evPersonCount?: number) => {
    if(!(evTitle.length || evTime))
      return alertAction('Por favor, ingrese un título y un tiempo en horas!');

    if(usar_Limites && !evPersonCount)
      return alertAction('Por favor, ingrese la cantidad de personas!');

    const endTime = getEndTime(dateStr, evTime);
    const hrStart = Number(dateStr.split('T')[1].split(':')[0]);
    const hrEnd = Number(endTime.split('T')[1].split(':')[0]);

    if(hrStart < inicio_Amenidad)
      return alertAction(`Las ${hrStart}hrs queda fuera del horario de la amenidad. (de ${inicio_Amenidad}hrs a ${fin_Amenidad}hrs)`);

    if(hrEnd > fin_Amenidad)
      return alertAction(`Las ${hrEnd}hrs queda fuera del horario de la amenidad. (de ${inicio_Amenidad}hrs a ${fin_Amenidad}hrs)`);

    if((hrEnd - hrStart) > tiempo_Maximo)
      return alertAction(`El tiempo máximo de la reserva debe ser ${tiempo_Maximo} horas`);

    const newEvent:IEventoCalendario = {
      id: evTitle ?? "no-event",
      title: evTitle ?? "No event",
      start: dateStr,
      end: endTime ?? "",
      cantidadPersonas: usar_Limites ? evPersonCount : undefined,
      precioCalculado: usar_Limites ? precioTotal : undefined
    }
    setEvents((prev:IEventoCalendario[]) => [...prev, newEvent]);
    setEventTitle('');
    setEventTime('');
    setPersonCount('');
    setActiveForm(false);
  };

  const handleClose = () => {
    setEventTitle('');
    setEventTime('');
    setPersonCount('');
    setActiveForm(false);
  };

  const handleButtonClick = (action: () => void) => () => {
    // Ejecutar la acción directamente sin eventos
    action();
  };

  // Prevenir scroll del body cuando el modal está abierto
  React.useEffect(() => {
    if (activeForm) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      // Limpiar cualquier evento pendiente
      const clearEvents = () => {
        // Forzar un re-render del modal
        setTimeout(() => {
          setActiveForm(true);
        }, 0);
      };
      
      clearEvents();
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    }

    // Cleanup cuando el componente se desmonta
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    };
  }, [activeForm]);

  if (!activeForm) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 35,
        backdropFilter: 'blur(4px)',
        overflow: 'hidden'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '90%',
          maxWidth: '400px',
          borderRadius: '20px',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          touchAction: 'none'
        }}
      >
                 {/* Header */}
         <Box
           sx={{
             background: 'linear-gradient(135deg, #013467 0%, #013467 100%)',
             color: 'white',
             p: 2,
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center'
           }}
         >
                     <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'inherit' }}>
             Crear Reserva
           </Typography>
          <IconButton
            onClick={handleButtonClick(handleClose)}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Form Body */}
        <Box sx={{ p: 3 }}>
          {/* Información de la amenidad */}
          {infoAmenidad && (
            <Box sx={{ mb: 3 }}>
                             <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#013467', mb: 1, fontFamily: 'inherit' }}>
                 {infoAmenidad.nombre}
               </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   <AccessTime sx={{ fontSize: 16, color: '#013467', mr: 1 }} />
                   <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                     Horario: {inicio_Amenidad}:00 - {fin_Amenidad}:00 hrs
                   </Typography>
                 </Box>
                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                   <AccessTime sx={{ fontSize: 16, color: '#013467', mr: 1 }} />
                   <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                     Máx. {tiempo_Maximo} hrs
                   </Typography>
                 </Box>
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Título */}
            <TextField
              fullWidth
              label="Título de la reserva"
              variant="outlined"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
                             InputProps={{
                 startAdornment: <Title sx={{ color: '#013467', mr: 1 }} />
               }}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   borderRadius: '12px',
                   fontFamily: 'inherit',
                   '&:hover fieldset': {
                     borderColor: '#013467'
                   },
                   '&.Mui-focused fieldset': {
                     borderColor: '#013467'
                   }
                 }
               }}
            />

            {/* Tiempo */}
            <TextField
              fullWidth
              label="Tiempo (horas)"
              type="number"
              variant="outlined"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
                             InputProps={{
                 startAdornment: <AccessTime sx={{ color: '#013467', mr: 1 }} />
               }}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   borderRadius: '12px',
                   fontFamily: 'inherit',
                   '&:hover fieldset': {
                     borderColor: '#013467'
                   },
                   '&.Mui-focused fieldset': {
                     borderColor: '#013467'
                   }
                 }
               }}
            />

            {/* Cantidad de personas (solo si usar_Limites está activo) */}
            {usar_Limites && (
              <TextField
                fullWidth
                label={`Cantidad de personas (máx. ${obtenerLimiteMaximo()})`}
                type="number"
                variant="outlined"
                value={personCount}
                onChange={(e) => setPersonCount(e.target.value)}
                inputProps={{
                  min: 1,
                  max: obtenerLimiteMaximo()
                }}
                               InputProps={{
                 startAdornment: <Group sx={{ color: '#013467', mr: 1 }} />
               }}
               sx={{
                 '& .MuiOutlinedInput-root': {
                   borderRadius: '12px',
                   fontFamily: 'inherit',
                   '&:hover fieldset': {
                     borderColor: '#013467'
                   },
                   '&.Mui-focused fieldset': {
                     borderColor: '#013467'
                   }
                 }
               }}
              />
            )}

            {/* Fecha seleccionada */}
                         <Box sx={{
               p: 2,
               borderRadius: '10px',
               background: '#f8f9fa',
               border: '1px solid #e9ecef',
               textAlign: 'center'
             }}>
               <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                 Fecha: {new Date(dateStr).toLocaleDateString('es-ES', {
                   weekday: 'long',
                   year: 'numeric',
                   month: 'long',
                   day: 'numeric'
                 })}
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                 Hora: {dateStr.split('T')[1].split(':')[0]}:00 hrs
               </Typography>
             </Box>

            {/* Precio total (solo cuando usar_Limites está activo y hay cantidad válida) */}
            {usar_Limites && personCount && parseInt(personCount) > 0 && (
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(1, 52, 103, 0.05) 0%, rgba(1, 52, 103, 0.1) 100%)',
                border: '1px solid rgba(1, 52, 103, 0.2)',
                textAlign: 'center'
              }}>
                {(() => {
                  const rangoInfo = obtenerRangoInfo(parseInt(personCount));
                  const limiteMax = obtenerLimiteMaximo();
                  return rangoInfo ? (
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#013467', fontFamily: 'inherit' }}>
                      Precio: Q{rangoInfo.precio.toFixed(2)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ fontFamily: 'inherit' }}>
                      Cantidad fuera de rango. Máximo permitido: {limiteMax} personas
                    </Typography>
                  );
                })()}
              </Box>
            )}
          </Box>

          {/* Submit Button */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                     <Button
              fullWidth
              variant="outlined"
              onClick={handleButtonClick(handleClose)}
              sx={{
                borderRadius: '12px',
                border: '2px solid #013467',
                color: '#013467',
                fontWeight: 'bold',
                fontFamily: 'inherit',
                '&:hover': {
                  background: '#013467',
                  color: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(1, 52, 103, 0.3)'
                }
              }}
            >
              Cancelar
            </Button>
                                     <Button
              fullWidth
              variant="contained"
              onClick={handleButtonClick(() => handleCreateEvent(eventTitle, Number(eventTime), Number(personCount)))}
              sx={{
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #013467 0%, #013467 100%)',
                fontWeight: 'bold',
                fontFamily: 'inherit',
                boxShadow: '0 4px 12px rgba(1, 52, 103, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(1, 52, 103, 0.4)'
                }
              }}
            >
              Crear Reserva
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};